import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import {
  slideFileFromSlide,
  slideThumbnailCandidatesFromFile,
  tileExtensionFromSlideFile,
} from "../showTile/tileUrl";

function probeImage(url) {
  return new Promise((resolve) => {
    if (!url) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.onload = () => resolve(url);
    img.onerror = () => resolve(null);
    img.referrerPolicy = "no-referrer";
    img.src = url;
  });
}

async function resolveSlideThumbUrl(slide, preferredUrl) {
  const file = slideFileFromSlide(slide);
  const candidates = slideThumbnailCandidatesFromFile(file, preferredUrl);

  // Prefer tiles.json extension / overview tile when the stitch job wrote a manifest.
  if (file?.url) {
    const prefix = String(file.url).replace(/\/+$/, "");
    try {
      const response = await fetch(`${prefix}/tiles.json`, { cache: "no-store" });
      if (response.ok) {
        const manifest = await response.json();
        const ext = String(manifest?.tile_ext || tileExtensionFromSlideFile(file) || "jpg").replace(
          /^\./,
          ""
        );
        const sessionMatch = String(file.name || file.alias || "").match(
          /(?:upload-photo|capture-grid-stitch)-([0-9a-f-]{8,})/i
        );
        const preferred = [];
        if (sessionMatch?.[1]) preferred.push(`${prefix}/_master/${sessionMatch[1]}.${ext}`);
        preferred.push(`${prefix}/_master/latest.${ext}`);
        preferred.push(`${prefix}/0/0/0.${ext}`);
        for (const url of preferred) {
          const hit = await probeImage(url);
          if (hit) return hit;
        }
      }
    } catch (_) {
      /* fall through to candidate list */
    }
  }

  for (const url of candidates) {
    const hit = await probeImage(url);
    if (hit) return hit;
  }
  return null;
}

/**
 * Slide card cover: probes master / overview tiles until one loads.
 * Avoids the browser broken-image icon on the library grid.
 */
export default function SlideCardThumb({
  slide,
  preferredUrl = null,
  alt = "",
  height,
  width,
  minWidth,
  emptyLabel = null,
  objectPosition = "center top",
  dark = false,
}) {
  const fileKey = useMemo(() => {
    const file = slideFileFromSlide(slide);
    return `${file?.url || ""}|${file?.name || ""}|${preferredUrl || ""}`;
  }, [slide, preferredUrl]);

  const [src, setSrc] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    setSrc(null);
    resolveSlideThumbUrl(slide, preferredUrl).then((url) => {
      if (cancelled) return;
      setSrc(url);
      setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [fileKey, slide, preferredUrl]);

  const frameSx = {
    position: "relative",
    width: width ?? "100%",
    minWidth,
    height,
    overflow: "hidden",
    margin: 0,
    padding: 0,
    lineHeight: 0,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: src
      ? "transparent"
      : dark
        ? "linear-gradient(160deg, rgba(35,38,42,0.99) 0%, rgba(22,24,28,0.99) 100%)"
        : "linear-gradient(160deg, rgba(250,251,253,0.99) 0%, rgba(232,235,241,0.99) 100%)",
  };

  if (!ready || !src) {
    return (
      <Box sx={frameSx} className="titles-card-thumb">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            color: dark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.42)",
          }}
        >
          <Icon sx={{ fontSize: emptyLabel ? 44 : 36, opacity: 0.85 }}>photo_library</Icon>
          {emptyLabel && ready ? (
            <Box
              component="span"
              sx={{
                fontSize: "0.7rem",
                fontWeight: 600,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
            >
              {emptyLabel}
            </Box>
          ) : null}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={frameSx} className="titles-card-thumb">
      <Box
        component="img"
        src={src}
        alt={alt}
        draggable={false}
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition,
          display: "block",
          margin: 0,
          padding: 0,
          boxSizing: "border-box",
        }}
      />
    </Box>
  );
}
